import { AxiosResponse } from 'axios';
import axios from '../helpers/axios';
import qs from 'querystring';
import { logError } from '../services';
import {
  IDropdownResponse,
  INews,
  IResponse,
  INewsPost,
  INewsDetail,
  INewsVisibilities
} from '../models';

export const getNews = async (filters?: {
  category?: string;
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  subjectSearch?: string;
  messageSearch?: string;
  username?: string;
  vendorId?: string | number;
  displayDateStartDate?: string;
  displayDateEndDate?: string;
  takeDownDateStartDate?: string;
  takeDownDateEndDate?: string;
  createdDateStartDate?: string;
  createdDateEndDate?: string;
  onlyActive?: boolean; // will only pull back news item for the logged in user
}): Promise<IResponse<INews[]>> => {
  try {
    const { data } = await axios.get(`/api/news-items`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getNews failed');
    return Promise.reject(error);
  }
};

export const getNewsItem = async (id: string | number): Promise<INewsDetail> => {
  try {
    const { data } = await axios.get(`/api/news-items/${id}`);

    return data;
  } catch (error: any) {
    return Promise.reject(error?.response?.data);
  }
};

export const createNewsItem = async (body: INewsPost): Promise<string> => {
  try {
    const { data } = await axios.post(`/api/news-items`, body);

    return data;
  } catch (error: any) {
    return Promise.reject(error?.response?.data);
  }
};

export const updateNewsItem = async (id: string | number, body: INewsPost): Promise<string> => {
  try {
    const { data } = await axios.put(`/api/news-items/${id}`, body);

    return data;
  } catch (error: any) {
    return Promise.reject(error?.response?.data);
  }
};

export const deleteNewsItem = async (id: string | number): Promise<any> => {
  try {
    const { data } = await axios.delete(`/api/news-items/${id}`);
    return data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getNewsCategories = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/news-items/categories`);

    return data;
  } catch (error: any) {
    return Promise.reject(error?.response?.data);
  }
};

export const getNewsVisibilities = async (): Promise<INewsVisibilities[]> => {
  try {
    const { data } = await axios.get(`/api/news-items/visibility`);

    return data;
  } catch (error: any) {
    return Promise.reject(error?.response?.data);
  }
};

export const getNewsItemTypes = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/news-items/types`);

    return data;
  } catch (error: any) {
    return Promise.reject(error?.response?.data);
  }
};

export const getNewsVideo = async (
  newsItemId: string | number,
  fromListPage: boolean
): Promise<AxiosResponse<string>> => {
  try {
    const res = await axios.get(`/api/news-items/${newsItemId}/video`, {
      params: { forList: fromListPage }
    });
    return res;
  } catch (error: any) {
    return Promise.reject(error.response.data);
  }
};

export const getNewsFile = async (
  newsItemId: string | number,
  fromListPage: boolean
): Promise<AxiosResponse> => {
  try {
    const data = await axios.get(`/api/news-items/${newsItemId}/file`, {
      responseType: 'blob',
      params: { forList: fromListPage }
    });
    return data;
  } catch (error: any) {
    return Promise.reject(error.response.data);
  }
};

export const createNewsFile = async (
  newsItemId: string | number,
  file: FormData | string
): Promise<string[]> => {
  try {
    const { data } = await axios.post(`/api/news-items/${newsItemId}/file`, file);
    return data;
  } catch (error) {
    return Promise.reject(error?.response?.data);
  }
};

export const getUnreadNewsItemCounts = async (): Promise<number> => {
  try {
    const { data } = await axios.get(`/api/news-items/unread-count`);
    return data;
  } catch (error: any) {
    return Promise.reject(error?.response?.data);
  }
};

export const markNewsItemRead = async (newsItemId: number | string): Promise<null> => {
  try {
    const { data } = await axios.post(`/api/news-items/${newsItemId}/read`);
    return data;
  } catch (error: any) {
    return Promise.reject(error?.response?.data);
  }
};
